import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  Fragment,
} from "react";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { useSearchParams } from "react-router-dom";
import { ChevronRight, Download, Info, List, Search } from "lucide-react";
import {
  PageHeader,
  PaginationControls,
  SortableHead,
  MetricCodeBadge,
  CategoryBadge,
} from "@/components/common";
import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FilterBar from "../components/FilterBar";
import { dashboardFiltersStore } from "@/features/esg-dashboard-v3/stores/filters.store";
import { targetSetupFiltersStore } from "../stores/target-setup-filters.store";
import { loadFilterOptions } from "@/features/esg-dashboard-v3/operations/load-filter-options.ops";
import { parseCarriedIds } from "@/features/esg/utils/scope-carry.utils";
import { resolveCarriedOrgIds } from "@/features/esg-dashboard-v3/utils/hierarchy-picker";
import ReportingPeriodLabel from "@/features/data-entry/components/common/ReportingPeriodLabel";
import { loadTargetSetupOverview } from "../operations/load-target-setup-overview.ops";
import { useTargetSetupOverviewStore } from "../stores/target-setup-overview.store";
import { targetSetupOverviewService } from "../services/target-setup-overview.service";
import OrgBreakdownPanel from "../components/OrgBreakdownPanel";
import { useTargetSetupStore } from "../hooks/useTargetSetupStore";
import {
  loadCompanyTargetFrequency,
  loadDisclosures,
  loadOrgList,
} from "../operations/target-setup.ops";
import ExportTargetSetupDialog from "../components/ExportTargetSetupDialog";
import TargetSetupNavTabs from "../components/TargetSetupNavTabs";
import PageTitle from "@/components/common/PageTitleBadge";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";
import { loadReportingYears } from "@/features/reporting-boundary/operations/reporting-boundary.ops";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { resolveOrgIdsExact } from "../domains/resolveOrgIdsExact";
import { buildOrgStatusInfoMap } from "@/components/common/organization.utils";

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// Stable empty set — prevents OrgBreakdownPanel from recomputing flatRows
// every render when no nodes have been collapsed yet for a given row.
const EMPTY_SET = new Set();

// The three tab states (filled/unfilled/all) get encoded/decoded in the URL's
// "filled" param and mapped to the export dialog's exportMode — kept as named
// helpers so the three encodings can't quietly drift out of sync.
const tabToFilledParam = (tab) =>
  tab === "filled" ? "true" : tab === "unfilled" ? "false" : "all";

const filledParamToTab = (filled) => {
  if (filled === "true") return "filled";
  if (filled === "false") return "unfilled";
  if (filled === "all") return "all";
  return null;
};

const tabToExportMode = (tab) =>
  tab === "unfilled" ? "empty" : tab === "all" ? "mixed" : "filled";

const normalizeFramework = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const extractFrameworkOptionCodes = (disclosure) => {
  const values = [
    disclosure?.framework,
    disclosure?.frameworkCode,
    disclosure?.framework_code,
    disclosure?.frameworks,
    disclosure?.frameworkCodes,
    disclosure?.framework_codes,
  ];

  const seen = new Set();
  const codes = [];

  values
    .flatMap((value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (typeof value === "object") {
        return [value.code, value.framework, value.value].filter(Boolean);
      }
      if (typeof value === "string") {
        const parts = /[,/|]/.test(value) ? value.split(/[,/|]+/) : [value];
        return parts;
      }

      return [String(value ?? "").trim()];
    })
    .forEach((code) => {
      const key = normalizeFramework(code);
      if (!key || seen.has(key)) return;
      seen.add(key);
      codes.push(code);
    });

  return codes;
};

const FilledProgress = ({
  filledOrgs = 0,
  totalOrgs = 0,
  isPartial = false,
}) => {
  const { t } = useTran();
  const raw = totalOrgs > 0 ? (filledOrgs / totalOrgs) * 100 : 0;
  const pct = Math.round(raw);
  const isFull = filledOrgs === totalOrgs && totalOrgs > 0;
  const label =
    filledOrgs > 0 && raw < 0.005
      ? "<0.01 %"
      : isFull
        ? "100 %"
        : `${raw.toFixed(2)} %`;
  const barPct = filledOrgs > 0 && pct === 0 ? 1 : pct;
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        {isPartial && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-amber-600" />
              </TooltipTrigger>
              <TooltipContent side="top" align="start">
                {t(
                  "Filled for {{filled}} of {{total}} organizations — this metric appears in both Filled and Unfilled tabs.",
                  {
                    filled: filledOrgs,
                    total: totalOrgs,
                  },
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <span className="w-14 shrink-0 text-right text-sm font-medium text-foreground whitespace-nowrap">
        {filledOrgs}/{totalOrgs}
      </span>
      <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-nxstart-green transition-all"
          style={{ width: `${barPct}%` }}
        />
      </div>
      <span
        className={`text-xs font-medium ${isFull ? "text-nxstart-green" : "text-muted-foreground"}`}
      >
        {label}
      </span>
    </div>
  );
};

const TableSkeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton key={i} className="h-10 w-full rounded" />
    ))}
  </div>
);

const TargetSetupOverviewPage = observer(() => {
  const { t } = useTran();
  const store = useTargetSetupOverviewStore();
  const targetStore = useTargetSetupStore();
  const [activeTab, setActiveTab] = useState("filled");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [orgData, setOrgData] = useState({});
  const fetchEpochRef = useRef({});
  const [sortField, setSortField] = useState("code");
  const [sortDir, setSortDir] = useState("asc");
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAppliedRef = useRef(false);

  const { reportingYears, availableFrameworks } = reportingYearStore;

  const frameworkLabelByCode = useMemo(
    () =>
      new Map(
        (availableFrameworks || [])
          .map((framework) => {
            const code =
              framework?.code ||
              framework?.framework ||
              framework?.value ||
              framework?.id;
            if (!code) return null;
            return [
              normalizeFramework(code),
              String(framework?.name || framework?.label || code).trim(),
            ];
          })
          .filter(Boolean),
      ),
    [availableFrameworks],
  );

  const yearFilteredDisclosures = useMemo(() => {
    const selectedYears = (targetSetupFiltersStore.filters.years ?? [])
      .map((year) => String(year ?? "").trim())
      .filter(Boolean);

    if (
      selectedYears.length === 0 ||
      selectedYears.includes("All") ||
      selectedYears.includes("all")
    ) {
      return targetStore.disclosures ?? [];
    }

    return (targetStore.disclosures ?? []).filter((disclosure) => {
      const disclosureYears = [
        ...(Array.isArray(disclosure?.masterDataYears)
          ? disclosure.masterDataYears
          : []),
        disclosure?.masterDataYear,
        ...(Array.isArray(disclosure?.masterDataRecords)
          ? disclosure.masterDataRecords.map((record) => record?.year)
          : []),
      ];

      return disclosureYears.some((year) =>
        selectedYears.includes(String(year)),
      );
    });
  }, [targetSetupFiltersStore.filters.years, targetStore.disclosures]);

  const frameworkOptions = useMemo(() => {
    const seen = new Set();
    const disclosureFrameworks = yearFilteredDisclosures
      .flatMap(extractFrameworkOptionCodes)
      .filter((code) => {
        const key = normalizeFramework(code);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((left, right) => {
        const labelLeft =
          frameworkLabelByCode.get(normalizeFramework(left)) || left;
        const labelRight =
          frameworkLabelByCode.get(normalizeFramework(right)) || right;
        return String(labelLeft).localeCompare(String(labelRight));
      });

    const options = disclosureFrameworks.map((framework) => ({
      value: framework,
      label:
        frameworkLabelByCode.get(normalizeFramework(framework)) || framework,
    }));

    if (options.length === 1) {
      return options;
    }

    return [{ label: t("All"), value: "__all__" }, ...options];
  }, [frameworkLabelByCode, t, yearFilteredDisclosures]);

  const orgStatusMap = useMemo(
    () =>
      buildOrgStatusInfoMap(targetSetupFiltersStore.available.orgTree || []),
    [targetSetupFiltersStore.available.orgTree],
  );

  const handleSort = useCallback(
    (field) => {
      if (sortField === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDir("asc");
      }
      setPage(1);
    },
    [sortField],
  );
  const [collapsedOrgNodes, setCollapsedOrgNodes] = useState({});

  const toggleOrgCollapse = useCallback((rowKey, nodeKey) => {
    setCollapsedOrgNodes((prev) => {
      const set = new Set(prev[rowKey] ?? []);
      if (set.has(nodeKey)) set.delete(nodeKey);
      else set.add(nodeKey);
      return { ...prev, [rowKey]: set };
    });
  }, []);

  const toggleRow = useCallback(
    (key, item) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
          return next;
        }
        next.add(key);
        return next;
      });
      setOrgData((prev) => {
        if (prev[key]) return prev;
        const epoch = (fetchEpochRef.current[key] ?? 0) + 1;
        fetchEpochRef.current[key] = epoch;
        const entry = { loading: true, orgs: [], error: null };
        const next = { ...prev, [key]: entry };
        // const { orgIds } = targetSetupFiltersStore.filters;
        // Resolved the same way as the overview list's own load
        // (load-target-setup-overview.ops.js) — exactly the picked org(s), not
        // expanded to their descendants — so the breakdown agrees with the
        // summary's filled/total counts about what "the selected org" means.
        const orgIds = resolveOrgIdsExact(
          targetSetupFiltersStore.filters.orgIds,
          targetSetupFiltersStore.available.orgTree,
        );
        targetSetupOverviewService
          .getOrgBreakdown({
            disclosureId: item.disclosureId,
            year: item.year,
            orgIds,
          })
          .then((res) => {
            if (fetchEpochRef.current[key] !== epoch) return;
            const orgsWithStatus = (res?.orgs ?? []).map((org) => {
              const info = orgStatusMap.get(String(org.orgId || org.id || ""));
              return {
                ...org,
                status: info?.status,
                inactiveDate: info?.inactiveDate,
              };
            });
            setOrgData((p) => ({
              ...p,
              [key]: {
                loading: false,
                orgs: orgsWithStatus,
                monthOptions: res?.monthOptions ?? [],
                targetFrequency:
                  res?.targetFrequency ?? item.targetFrequency ?? "yearly",
                error: null,
              },
            }));
          })
          .catch((err) => {
            if (fetchEpochRef.current[key] !== epoch) return;
            setOrgData((p) => ({
              ...p,
              [key]: {
                loading: false,
                orgs: [],
                error: err?.message ?? "Failed",
              },
            }));
          });
        return next;
      });
    },
    [orgStatusMap],
  );

  const resetPage = useCallback(() => setPage(1), []);

  useEffect(() => {
    // Seed this page's own filters instance from the dashboard's current
    // values (read-only copy: target-setup edits stay local and can never
    // rewrite the dashboard's filters), then apply organization/year from
    // URL (ESG dashboard "Target Setup Completion" links) before the first
    // load, so the initial fetch already reflects that scope.
    // Synthetic country:* picker ids resolve to real org ids NOW, against
    // the dashboard's already-loaded tree — this store's own tree has not
    // loaded yet, and resolving tokens against an empty tree would drop the
    // scope entirely. With the tokens gone the country lens has nothing to
    // display, so the seeded hierarchy falls back to "all".
    const dashboardFilters = dashboardFiltersStore.filters;
    const hadCountryTokens = (dashboardFilters.orgIds ?? []).some(
      (id) => typeof id === "string" && id.startsWith("country:"),
    );
    targetSetupFiltersStore.seedFrom({
      ...dashboardFilters,
      orgIds: resolveCarriedOrgIds(
        dashboardFilters.orgIds,
        dashboardFiltersStore.available.orgTree,
      ),
      hierarchy: hadCountryTokens ? "all" : dashboardFilters.hierarchy,
    });

    // A present param always applies, even when it parses to [] ("all"):
    // a bookmarked all-organizations link must override the seeded scope.
    const orgParam = searchParams.get("organization");
    if (orgParam !== null) {
      // URL-carried real ids are only displayable under the unlensed
      // picker, and the lens selector is hidden on this page. Hierarchy
      // must be set first: setFilter("hierarchy") clears orgIds.
      targetSetupFiltersStore.setFilter("hierarchy", "all");
      targetSetupFiltersStore.setFilter("orgIds", parseCarriedIds(orgParam));
    }
    const yearParam = searchParams.get("year");
    if (yearParam !== null) {
      targetSetupFiltersStore.setFilter(
        "years",
        parseCarriedIds(yearParam).map(Number).filter(Number.isFinite),
      );
    }

    loadFilterOptions(targetSetupFiltersStore);
    loadCompanyTargetFrequency();
    loadTargetSetupOverview();
    loadDisclosures();
    loadOrgList();
    loadReportingYears();

    const dispose = reaction(
      () => ({
        years: targetSetupFiltersStore.filters.years.slice(),
        frameworks: targetSetupFiltersStore.filters.frameworks.slice(),
        categories: targetSetupFiltersStore.filters.categories.slice(),
        orgIds: targetSetupFiltersStore.filters.orgIds.slice(),
      }),
      () => {
        resetPage();
        // Expanded rows and cached org data are filter-scoped — clear both
        // so stale breakdowns from the previous filter are not displayed.
        fetchEpochRef.current = Object.fromEntries(
          Object.entries(fetchEpochRef.current).map(([k, v]) => [k, v + 1]),
        );
        setExpandedRows(new Set());
        setOrgData({});
        loadTargetSetupOverview();
      },
      { delay: 500 },
    );

    return () => {
      dispose();
      store.reset();
    };
  }, []);

  // Auto-select/expand the linked metric from URL query params — organization
  // and year are applied to targetSetupFiltersStore in the mount effect above;
  // this handles the two params that only make sense once items have loaded.
  // Gated on hasLoadedOnce rather than items.length so a genuinely empty
  // first load still marks this as applied — otherwise a later, unrelated
  // filter change that produces items would re-trigger this and yank the
  // user back to the originally-linked metric.
  useEffect(() => {
    if (urlAppliedRef.current || !store.hasLoadedOnce) return;

    const tabFromUrl = filledParamToTab(searchParams.get("filled"));
    if (tabFromUrl) setActiveTab(tabFromUrl);

    const metricId = searchParams.get("metricId");
    if (metricId) {
      // Multi-value scope-carry contract: [] means "all" / no year filter.
      const carriedYears = parseCarriedIds(searchParams.get("year"));
      const match = store.items.find(
        (item) =>
          item.disclosureId === metricId &&
          (carriedYears.length === 0 ||
            carriedYears.includes(String(item.year))),
      );
      if (match) {
        setSearch(match.code || "");
        const rowKey = `${match.disclosureId ?? match.code ?? "unknown"}-${match.year}`;
        toggleRow(rowKey, match);
      }
    }

    urlAppliedRef.current = true;
  }, [store.hasLoadedOnce, store.items]);

  // Old Code
  // const exportYearOptions = useMemo(() => {
  //   const boundary = targetStore.reportingBoundary;
  //   const startYear = Number.parseInt(boundary?.startYear, 10);
  //   const endYear = Number.parseInt(boundary?.endYear, 10);
  //   const years = [];
  //   if (Number.isFinite(startYear) && Number.isFinite(endYear)) {
  //     const minYear = Math.min(startYear, endYear);
  //     const maxYear = Math.max(startYear, endYear);
  //     for (let year = maxYear; year >= minYear; year -= 1) {
  //       years.push({ label: String(year), value: String(year) });
  //     }
  //   }
  //   return years;
  // }, [targetStore.reportingBoundary]);

  // const exportDefaultYear = useMemo(() => {
  //   const boundaryEndYear = Number.parseInt(
  //     targetStore.reportingBoundary?.endYear,
  //     10,
  //   );
  //   if (Number.isFinite(boundaryEndYear)) return String(boundaryEndYear);
  //   return exportYearOptions[0]?.value || "";
  // }, [exportYearOptions, targetStore.reportingBoundary?.endYear]);

  const exportYearOptions = useMemo(() => {
    return (reportingYears || [])
      .slice()
      .sort((a, b) => b.startYear - a.startYear)
      .map((ry) => ({
        label: formatReportingYearPeriod(ry),
        value: String(ry.startYear),
      }));
  }, [reportingYears]);

  const exportDefaultYear = useMemo(() => {
    if (!reportingYears?.length) {
      return exportYearOptions[0]?.value || "";
    }
    const activeYear = targetSetupFiltersStore.filters.years[0];
    if (activeYear !== undefined && activeYear !== null) {
      return String(activeYear);
    }
    const latestReportingYear = [...reportingYears].sort((a, b) => {
      // compare by reporting period end date
      const aDate = new Date(a.endYear, a.endMonth - 1);
      const bDate = new Date(b.endYear, b.endMonth - 1);

      return bDate - aDate;
    })[0];

    return String(latestReportingYear.startYear);
  }, [
    reportingYears,
    exportYearOptions,
    targetSetupFiltersStore.filters.years,
  ]);
  const exportOrgIds = useMemo(
    () =>
      resolveOrgIdsExact(
        targetSetupFiltersStore.filters.orgIds,
        targetSetupFiltersStore.available.orgTree,
      ),
    [
      targetSetupFiltersStore.filters.orgIds,
      targetSetupFiltersStore.available.orgTree,
    ],
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    resetPage();
    setExpandedRows(new Set());
    setOrgData({});

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("filled", tabToFilledParam(tab));
    setSearchParams(nextParams, { replace: true });
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    resetPage();
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetPage();
  };

  const searchQuery = useMemo(() => search.trim().toLowerCase(), [search]);

  const matchSearch = useCallback(
    (item) => {
      if (!searchQuery) return true;
      return (
        item.code?.toLowerCase().includes(searchQuery) ||
        item.title?.toLowerCase().includes(searchQuery)
      );
    },
    [searchQuery],
  );

  const filteredFilled = useMemo(
    () => store.filledItems.filter(matchSearch),
    [store.filledItems, matchSearch],
  );
  const filteredUnfilled = useMemo(
    () => store.unfilledItems.filter(matchSearch),
    [store.unfilledItems, matchSearch],
  );
  const filteredAll = useMemo(
    () => store.items.filter(matchSearch),
    [store.items, matchSearch],
  );

  const baseRows = useMemo(
    () =>
      activeTab === "filled"
        ? filteredFilled
        : activeTab === "unfilled"
          ? filteredUnfilled
          : filteredAll,
    [activeTab, filteredFilled, filteredUnfilled, filteredAll],
  );

  const allRows = useMemo(() => {
    if (!sortField) return baseRows;
    return [...baseRows].sort((a, b) => {
      let av, bv;
      if (sortField === "pct") {
        av = a.totalOrgs > 0 ? a.filledOrgs / a.totalOrgs : 0;
        bv = b.totalOrgs > 0 ? b.filledOrgs / b.totalOrgs : 0;
      } else if (sortField === "year") {
        av = a.year;
        bv = b.year;
      } else {
        av = (a[sortField] ?? "").toString().toLowerCase();
        bv = (b[sortField] ?? "").toString().toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [baseRows, sortField, sortDir]);

  const totalRows = allRows.length;
  const uniqueMetrics = useMemo(
    () => new Set(allRows.map((item) => item.disclosureId)).size,
    [allRows],
  );
  const rows = allRows.slice((page - 1) * pageSize, page * pageSize);

  const partialCount = useMemo(
    () =>
      filteredAll.filter(
        (item) =>
          (item.filledOrgs ?? 0) > 0 &&
          (item.filledOrgs ?? 0) < (item.totalOrgs ?? 0),
      ).length,
    [filteredAll],
  );

  const orgStats = useMemo(
    () => ({
      filled: filteredFilled.reduce(
        (sum, item) => sum + (item.filledOrgs ?? 0),
        0,
      ),
      unfilled: filteredUnfilled.reduce(
        (sum, item) => sum + ((item.totalOrgs ?? 0) - (item.filledOrgs ?? 0)),
        0,
      ),
      allFilled: filteredAll.reduce(
        (sum, item) => sum + (item.filledOrgs ?? 0),
        0,
      ),
      allTotal: filteredAll.reduce(
        (sum, item) => sum + (item.totalOrgs ?? 0),
        0,
      ),
    }),
    [filteredFilled, filteredUnfilled, filteredAll],
  );

  const frameworkNameByCode = useMemo(
    () =>
      new Map(
        targetSetupFiltersStore.available.frameworkObjects.map((f) => [
          f.code,
          f.name,
        ]),
      ),
    [targetSetupFiltersStore.available.frameworkObjects],
  );

  const filterContextParts = useMemo(() => {
    const { years, frameworks, categories, orgIds } =
      targetSetupFiltersStore.filters;
    const parts = [];
    parts.push(years.length > 0 ? `Year ${years.join(", ")}` : "All Years");
    parts.push(
      orgIds.length > 0
        ? `${orgIds.length} organization${orgIds.length > 1 ? "s" : ""}`
        : "All Organizations",
    );
    parts.push(
      frameworks.length > 0
        ? frameworks
            .map((code) => frameworkNameByCode.get(code) ?? code)
            .join(", ")
        : "All Frameworks",
    );
    parts.push(
      categories.length > 0 ? categories.join(", ") : "All Categories",
    );
    if (searchQuery) parts.push(`matching "${searchQuery}"`);
    return parts;
  }, [
    targetSetupFiltersStore.filters.years,
    targetSetupFiltersStore.filters.orgIds,
    targetSetupFiltersStore.filters.frameworks,
    targetSetupFiltersStore.filters.categories,
    frameworkNameByCode,
    searchQuery,
  ]);

  const frequencyBadge = targetStore.targetFrequencyLoaded
    ? targetStore.targetFrequency === "monthly"
      ? t("Monthly")
      : t("Yearly")
    : null;

  const pageTitle = (
    <PageTitle
      title={t("Target Setup Overview")}
      frequencyBadge={frequencyBadge}
    />
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-4">
      <PageHeader
        title={pageTitle}
        description={t(
          "View all target setup records across organizations, filtered by reporting year, framework, and category.",
        )}
        icon={List}
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => setIsExportDialogOpen(true)}
              disabled={exportYearOptions.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {t("Excel Export")}
            </Button>
            <TargetSetupNavTabs />
          </div>
        }
      />

      <FilterBar hideFilterBy frameworkOptions={frameworkOptions} />

      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between gap-4 border-b px-4 py-3">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="filled">
                {t("Filled Targets")}
                {store.hasLoadedOnce && (
                  <Badge
                    variant="secondary"
                    className="ml-2 px-1.5 py-0 text-[10px]"
                  >
                    {filteredFilled.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="unfilled">
                {t("Unfilled Targets")}
                {store.hasLoadedOnce && (
                  <Badge
                    variant="secondary"
                    className="ml-2 px-1.5 py-0 text-[10px]"
                  >
                    {filteredUnfilled.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="all">
                {t("All Targets")}
                {store.hasLoadedOnce && (
                  <Badge
                    variant="secondary"
                    className="ml-2 px-1.5 py-0 text-[10px]"
                  >
                    {filteredAll.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2 ml-auto">
            {store.hasLoadedOnce && (
              <div className="flex items-center gap-1.5 rounded-full border border-sky-500 px-2.5 py-1 text-xs text-sky-500 whitespace-nowrap">
                <span>{t("Unique Assigned Metrics")}</span>
                <span className="rounded-full bg-sky-500 px-2 py-0.5 text-white">
                  {uniqueMetrics}
                </span>
              </div>
            )}
            <div className="relative w-64 shrink-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={handleSearchChange}
                placeholder={t("Search by code or title...")}
                className="pl-9"
              />
            </div>
          </div>
        </div>

        <div className="p-4">
          {store.loading ? (
            <TableSkeleton />
          ) : (
            <div className="space-y-4">
              {store.hasLoadedOnce && (
                <p className="text-sm text-muted-foreground">
                  {activeTab === "filled" && (
                    <>
                      <span className="font-semibold text-foreground">
                        {uniqueMetrics}
                      </span>{" "}
                      {t("unique metrics with")}{" "}
                      <span className="font-semibold text-emerald-600">
                        {filteredFilled.length}
                      </span>{" "}
                      {t("filled records")}
                      {" · "}
                      <span className="font-semibold text-emerald-600">
                        {orgStats.filled.toLocaleString()}
                      </span>{" "}
                      {t("organizational targets filled for")}{" "}
                      {filterContextParts.map((part, i) => (
                        <Fragment key={i}>
                          {i > 0 && (
                            <span className="text-muted-foreground"> · </span>
                          )}
                          <span className="inline-block rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">
                            {part}
                          </span>
                        </Fragment>
                      ))}
                      .
                      {partialCount > 0 && (
                        <>
                          {" "}
                          <span className="font-semibold text-amber-600">
                            {partialCount}
                          </span>{" "}
                          {t(
                            "of these are partially filled and also appear under Unfilled Targets.",
                          )}
                        </>
                      )}
                    </>
                  )}
                  {activeTab === "unfilled" && (
                    <>
                      <span className="font-semibold text-foreground">
                        {uniqueMetrics}
                      </span>{" "}
                      {t("unique metrics have")}{" "}
                      <span className="font-semibold text-amber-600">
                        {filteredUnfilled.length}
                      </span>{" "}
                      {t("records with no target set")}
                      {" · "}
                      <span className="font-semibold text-amber-600">
                        {orgStats.unfilled.toLocaleString()}
                      </span>{" "}
                      {t("organizational targets unfilled for")}{" "}
                      {filterContextParts.map((part, i) => (
                        <Fragment key={i}>
                          {i > 0 && (
                            <span className="text-muted-foreground"> · </span>
                          )}
                          <span className="inline-block rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">
                            {part}
                          </span>
                        </Fragment>
                      ))}
                      .
                      {partialCount > 0 && (
                        <>
                          {" "}
                          <span className="font-semibold text-amber-600">
                            {partialCount}
                          </span>{" "}
                          {t(
                            "of these are partially filled and also appear under Filled Targets.",
                          )}
                        </>
                      )}
                    </>
                  )}
                  {activeTab === "all" && (
                    <>
                      <span className="font-semibold text-foreground">
                        {uniqueMetrics}
                      </span>{" "}
                      {t("unique metrics for")}{" "}
                      {filterContextParts.map((part, i) => (
                        <Fragment key={i}>
                          {i > 0 && (
                            <span className="text-muted-foreground"> · </span>
                          )}
                          <span className="inline-block rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">
                            {part}
                          </span>
                        </Fragment>
                      ))}{" "}
                      —{" "}
                      <span className="font-semibold text-emerald-600">
                        {orgStats.allFilled.toLocaleString()}
                      </span>
                      {"/"}
                      <span className="font-semibold text-foreground">
                        {orgStats.allTotal.toLocaleString()}
                      </span>{" "}
                      {t("organizational targets filled.")}
                    </>
                  )}
                </p>
              )}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <SortableHead
                        field="code"
                        label={t("Code")}
                        className="w-24"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableHead
                        field="title"
                        label={t("Title")}
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableHead
                        field="category"
                        label={t("Category")}
                        className="w-36"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableHead
                        field="framework"
                        label={t("Framework")}
                        className="w-28"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableHead
                        field="year"
                        label={t("Reporting Year")}
                        className="w-44"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableHead
                        field="pct"
                        label={t("Organizations with Target")}
                        className="w-56"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="h-32 text-center text-muted-foreground"
                        >
                          {t("No records found.")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((item) => {
                        const rowKey = `${item.disclosureId ?? item.code ?? "unknown"}-${item.year}${item.month ? `-${item.month}` : ""}`;
                        const isExpanded = expandedRows.has(rowKey);
                        const orgEntry = orgData[rowKey];
                        const orgs = orgEntry?.orgs ?? [];
                        const isPartial =
                          (item.filledOrgs ?? 0) > 0 &&
                          (item.filledOrgs ?? 0) < (item.totalOrgs ?? 0);
                        return (
                          <Fragment key={rowKey}>
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => toggleRow(rowKey, item)}
                            >
                              <TableCell className="w-8 pr-0">
                                <ChevronRight
                                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
                                />
                              </TableCell>
                              <TableCell>
                                <MetricCodeBadge
                                  code={item.code}
                                  category={item.category}
                                />
                              </TableCell>
                              <TableCell className="max-w-xs">
                                <div className="flex items-start gap-2">
                                  <span className="line-clamp-2 text-sm">
                                    {item.title}
                                  </span>
                                  {/* <Badge
                                    variant="outline"
                                    className="shrink-0 px-1.5 py-0 text-[10px] font-medium"
                                  >
                                    {item.targetFrequency === "monthly"
                                      ? t("Monthly")
                                      : t("Yearly")}
                                  </Badge> */}
                                </div>
                                {item.isCalculated && item.formula && (
                                  <span className="block truncate text-xs text-muted-foreground">
                                    Calculated: {item.formula}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <CategoryBadge category={item.category} />
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {frameworkNameByCode.get(item.framework) ??
                                  item.framework}
                              </TableCell>
                              <TableCell className="text-sm">
                                {item.monthLabel ? (
                                  <span className="leading-tight font-medium text-foreground whitespace-nowrap">
                                    {item.monthLabel}
                                  </span>
                                ) : (
                                  <ReportingPeriodLabel
                                    reportingYears={
                                      targetSetupFiltersStore.available
                                        .reportingYears
                                    }
                                    year={item.year}
                                    className="leading-tight"
                                    yearClassName="font-medium text-foreground"
                                    periodClassName="font-normal whitespace-nowrap"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <FilledProgress
                                  filledOrgs={item.filledOrgs}
                                  totalOrgs={item.totalOrgs}
                                  isPartial={isPartial}
                                />
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={7} className="p-0">
                                  <div className="border-t bg-muted/20">
                                    {orgEntry?.loading ? (
                                      <div className="flex items-center gap-2 px-10 py-3 text-xs text-muted-foreground">
                                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                                        {t("Loading...")}
                                      </div>
                                    ) : orgEntry?.error ? (
                                      <p className="px-10 py-3 text-xs text-destructive">
                                        {orgEntry.error}
                                      </p>
                                    ) : orgs.length === 0 ? (
                                      <p className="px-10 py-3 text-xs text-muted-foreground">
                                        {t("No organizations.")}
                                      </p>
                                    ) : (
                                      <OrgBreakdownPanel
                                        orgs={orgs}
                                        monthOptions={
                                          orgEntry?.monthOptions ?? []
                                        }
                                        targetFrequency={
                                          orgEntry?.targetFrequency ??
                                          item.targetFrequency ??
                                          "yearly"
                                        }
                                        collapsed={
                                          collapsedOrgNodes[rowKey] ?? EMPTY_SET
                                        }
                                        onToggle={(nodeKey) =>
                                          toggleOrgCollapse(rowKey, nodeKey)
                                        }
                                      />
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalRows > 0 && (
                <PaginationControls
                  page={page}
                  pageSize={pageSize}
                  total={totalRows}
                  onPageChange={setPage}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  disabled={store.loading}
                  className="border-t pt-4"
                />
              )}
            </div>
          )}
        </div>
      </div>

      <ExportTargetSetupDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        defaultYear={exportDefaultYear}
        yearOptions={exportYearOptions}
        reportingYears={reportingYears}
        defaultFramework={targetSetupFiltersStore.filters.frameworks[0] || ""}
        defaultCategory={targetSetupFiltersStore.filters.categories[0] || ""}
        availableFrameworks={availableFrameworks}
        defaultOrgIds={exportOrgIds}
        defaultExportMode={tabToExportMode(activeTab)}
      />
    </div>
  );
});

export default TargetSetupOverviewPage;
